/**
 * ContractReviewPanel (P341) -- renewal / review posture chips + the
 * flagged contracts, rendered above the register list. Reads
 * GET /v1/admin/foundation/outsourcing-contracts/review-scan.
 */
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { api } from "shared/api/client";

interface ReviewRow {
  contract_id: string;
  contract_ref: string;
  title: string;
  criticality: string;
  material_outsourcing: boolean;
  end_date: string | null;
  next_review_date: string | null;
  status: string;
}

interface ReviewScan {
  generated_at: string;
  rows: ReviewRow[];
  counts: Record<string, number>;
}

const FLAGGED = [
  "expired",
  "expiring_soon",
  "review_overdue",
  "review_due_soon",
] as const;

const STATUS_LABEL: Record<string, string> = {
  expired: "Expired",
  expiring_soon: "Expiring soon",
  review_overdue: "Review overdue",
  review_due_soon: "Review due soon",
};

const STATUS_TONE: Record<string, string> = {
  expired: "bg-rose-100 text-rose-900 ring-rose-300",
  expiring_soon: "bg-amber-100 text-amber-900 ring-amber-300",
  review_overdue: "bg-rose-100 text-rose-900 ring-rose-300",
  review_due_soon: "bg-amber-100 text-amber-900 ring-amber-300",
};

export function ContractReviewPanel() {
  const { t } = useTranslation();
  const scan = useQuery({
    queryKey: ["foundation-admin", "outsourcing-contracts", "review-scan"],
    queryFn: () =>
      api.get<ReviewScan>("/v1/admin/foundation/outsourcing-contracts/review-scan"),
    staleTime: 60_000,
  });

  const data = scan.data;
  if (!data || !Array.isArray(data.rows)) return null;
  const flagged = data.rows.filter((r) =>
    (FLAGGED as readonly string[]).includes(r.status),
  );
  if (flagged.length === 0) return null;

  return (
    <section
      data-testid="contract-review-panel"
      className="mb-4 rounded-lg border border-amber-200 bg-amber-50/50 p-3"
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-slate-800">
          {t("screens.contractReviewPanel.renewalReviewAttention")}
        </span>
        {FLAGGED.filter((s) => (data.counts[s] ?? 0) > 0).map((s) => (
          <span
            key={s}
            data-testid={`contract-review-chip-${s}`}
            className={`rounded px-2 py-0.5 text-xs font-semibold ring-1 ${STATUS_TONE[s]}`}
          >
            {STATUS_LABEL[s]} · {data.counts[s]}
          </span>
        ))}
      </div>
      <ul className="space-y-1">
        {flagged.map((r) => (
          <li
            key={r.contract_id}
            data-testid={`contract-review-row-${r.contract_ref}`}
            className="flex flex-wrap items-baseline gap-x-3 text-xs text-slate-700"
          >
            <span className="font-mono">{r.contract_ref}</span>
            <span className="font-medium text-slate-900">{r.title}</span>
            <span
              className={`rounded px-1.5 py-0.5 font-semibold ring-1 ${STATUS_TONE[r.status]}`}
            >
              {STATUS_LABEL[r.status]}
            </span>
            <span className="text-slate-500">
              {r.status.startsWith("review")
                ? `review ${r.next_review_date ?? "--"}`
                : `ends ${r.end_date ?? "--"}`}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
